import React from 'react';
import { Field } from '../../components/ui';
import StepNav from './StepNav';

const optionsOf = (list) => (list || []).map(o => (
  <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
));

// Desktop "Setup Event" wizard: Function → Guests → Members, in one flow.
// One card of a readable width; each step's fields on as few lines as they
// fit, every box only as wide as its content. Enter in a one-line box runs the
// step's main button (Save & Add Guests / + Add & Add Another).
export default function SetupDesktop({ vm }) {
  const {
    step, saving, canFunctions, canGuests,
    functions, fnOptions, activeFn, activeFnId, setActiveFnId, pickExistingFunction,
    fnForm, setFn, saveFunction,
    guestForm, setGuest, saveGuest, guestsAdded,
    memberForm, setMember, saveMember, membersAdded,
    FUNCTION_TYPE_OPTIONS, goStep,
    canImport, importGuestContact, importMemberContact,
  } = vm;

  const enter = (fn) => (e) => { if (e.key === 'Enter') { e.preventDefault(); fn(); } };
  const isOther = fnForm.FunctionType === 'Other';

  // The guest / member line is the same shape in steps 2 and 3.
  const personRow = (form, set, onEnter, nameLabel) => (
    <div className="ev-row ev-setup-guest">
      <label className="field">{nameLabel} *
        <input autoFocus value={form.GuestName ?? ''} placeholder="Full name"
          onChange={(e) => set('GuestName', e.target.value)} onKeyDown={onEnter} />
      </label>
      <label className="field">Mobile Number
        <input type="tel" inputMode="numeric" value={form.MobileNumber ?? ''} placeholder="10 digits"
          onChange={(e) => set('MobileNumber', e.target.value)} onKeyDown={onEnter} />
      </label>
      <label className="field">Relation
        <input value={form.Relation ?? ''} placeholder="Uncle, friend…"
          onChange={(e) => set('Relation', e.target.value)} onKeyDown={onEnter} />
      </label>
      <label className="field">Pax
        <input className="ev-pax" type="number" inputMode="decimal" value={form.ExpectedCount ?? ''}
          onChange={(e) => set('ExpectedCount', e.target.value)} onKeyDown={onEnter} />
      </label>
    </div>
  );

  return (
    <div className="ev-setup">
      <div className="page-head"><h1>Setup Event</h1></div>
      <StepNav vm={vm} />

      {/* ── Step 1: Add Function ─────────────────────────────────────────── */}
      {step === 1 && (
        <div className="card card-pad">
          {!canFunctions ? (
            <div className="empty">Only the Event Owner / Admin can add functions. Skip to step 2 to add guests.</div>
          ) : (
            <>
              <h3>Step 1 · Add a Function</h3>
              <div className={'ev-row ev-setup-r1' + (isOther ? ' with-other' : '')}>
                <label className="field">Function Name *
                  <input autoFocus value={fnForm.FunctionName ?? ''} placeholder="e.g. Haldi"
                    onChange={(e) => setFn('FunctionName', e.target.value)} onKeyDown={enter(saveFunction)} />
                </label>
                <label className="field">Type
                  <select value={fnForm.FunctionType ?? ''} onChange={(e) => setFn('FunctionType', e.target.value)}>
                    <option value="">- select -</option>
                    {optionsOf(FUNCTION_TYPE_OPTIONS)}
                  </select>
                </label>
                {isOther && (
                  <label className="field">New type name
                    <input value={fnForm.FunctionTypeOther ?? ''}
                      onChange={(e) => setFn('FunctionTypeOther', e.target.value)} onKeyDown={enter(saveFunction)} />
                  </label>
                )}
              </div>
              <div className="ev-row ev-setup-r2">
                <label className="field">Date
                  <input type="date" value={fnForm.FunctionDate ?? ''} onChange={(e) => setFn('FunctionDate', e.target.value)} />
                </label>
                <Field label="Time" type="time" value={fnForm.FunctionTime} onChange={v => setFn('FunctionTime', v)} />
                <label className="field">Venue
                  <input value={fnForm.Venue ?? ''} placeholder="Hall / lawn / home"
                    onChange={(e) => setFn('Venue', e.target.value)} onKeyDown={enter(saveFunction)} />
                </label>
                <label className="field">City
                  <input value={fnForm.City ?? ''}
                    onChange={(e) => setFn('City', e.target.value)} onKeyDown={enter(saveFunction)} />
                </label>
              </div>
              <div className="ev-row ev-setup-r3">
                <label className="field">Venue Address
                  <textarea rows={2} value={fnForm.VenueAddress ?? ''} onChange={(e) => setFn('VenueAddress', e.target.value)} />
                </label>
                <label className="field">GPS Location (lat,lng or Maps link — shown to guests)
                  <textarea rows={2} value={fnForm.GpsLocation ?? ''} onChange={(e) => setFn('GpsLocation', e.target.value)} />
                </label>
              </div>
              <div className="ev-setup-actions">
                <button className="btn primary" disabled={saving} onClick={saveFunction}>
                  {saving ? 'Saving…' : 'Save & Add Guests →'}
                </button>
              </div>

              {functions.length > 0 && (
                <div className="ev-setup-pick">
                  <div className="ev-hint">…or pick an existing function to add guests:</div>
                  <div className="ev-setup-pick-list">
                    {functions.map(f => (
                      <button key={f.FunctionId} className="btn light sm" onClick={() => pickExistingFunction(f.FunctionId)}>
                        {f.FunctionName} →
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Step 2: Add Guests in the function ───────────────────────────── */}
      {step === 2 && (
        <div className="card card-pad">
          {!canGuests ? (
            <div className="empty">You don't have permission to add guests.</div>
          ) : (
            <>
              <h3>
                Step 2 · Add Guests {activeFn ? <span className="muted" style={{ fontWeight: 400 }}>into “{activeFn.FunctionName}”</span> : ''}
              </h3>
              <div className="ev-row ev-setup-fn">
                <label className="field">Function *
                  <select value={activeFnId ?? ''} onChange={(e) => setActiveFnId(e.target.value)}>
                    <option value="">- select -</option>
                    {optionsOf(fnOptions)}
                  </select>
                </label>
                {canImport && <button type="button" className="btn light sm" onClick={importGuestContact}>📇 Phone book</button>}
              </div>
              {personRow(guestForm, setGuest, enter(() => saveGuest({ keepGoing: true })), 'Guest Name')}
              <div className="ev-setup-actions">
                <button className="btn primary" disabled={saving} onClick={() => saveGuest({ keepGoing: true })}>
                  {saving ? 'Saving…' : '+ Add & Add Another'}
                </button>
                <button className="btn light" disabled={saving} onClick={() => saveGuest({ keepGoing: false })}>
                  Add & Go to Members →
                </button>
                <button className="btn light sm" onClick={() => goStep(3)}>Skip to Members →</button>
                <span className="ev-summary"><strong>{guestsAdded}</strong> added this session</span>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Step 3: Add Members for the event ────────────────────────────── */}
      {step === 3 && (
        <div className="card card-pad">
          {!canGuests ? (
            <div className="empty">You don't have permission to add members.</div>
          ) : (
            <>
              <h3>Step 3 · Add Members for the Event</h3>
              <div className="ev-hint">
                A member is a person invited to the event — pick which function they're invited to.
              </div>
              <div className="ev-row ev-setup-fn">
                <label className="field">Function *
                  <select value={memberForm.FunctionId ?? ''} onChange={(e) => setMember('FunctionId', e.target.value)}>
                    <option value="">- select -</option>
                    {optionsOf(fnOptions)}
                  </select>
                </label>
                {canImport && <button type="button" className="btn light sm" onClick={importMemberContact}>📇 Phone book</button>}
              </div>
              {personRow(memberForm, setMember, enter(() => saveMember({ keepGoing: true })), 'Member Name')}
              <div className="ev-setup-actions">
                <button className="btn primary" disabled={saving} onClick={() => saveMember({ keepGoing: true })}>
                  {saving ? 'Saving…' : '+ Add & Add Another'}
                </button>
                <button className="btn light" disabled={saving} onClick={() => saveMember({ keepGoing: false })}>
                  Add & Finish
                </button>
                <span className="ev-summary"><strong>{membersAdded}</strong> added this session</span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
